import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../ui/sheet';
import { Menu } from 'lucide-react';
import { companyInfo } from '../../data/mock';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { name: 'Products', href: '/products' },
  { name: 'Services', href: '/services' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Markets', href: '/markets' },
  { name: 'Compliance', href: '/compliance' },
  { name: 'News & Insights', href: '/insights' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-slate-200'
          : 'bg-transparent'
      }`}
    >
      {/* Top Banner */}
      <div className={`text-center py-1.5 text-xs font-medium transition-all duration-300 ${
        isScrolled ? 'bg-teal-600 text-white' : 'bg-white/10 text-white/90'
      }`}>
        🇦🇺 Proudly Australian | Serving businesses nationwide
      </div>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img 
              src={isScrolled ? "/images/logo.svg" : "/images/logo-dark.png"}
              alt="Naviton Global" 
              className="h-12 md:h-16 w-auto transition-all duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  location.pathname === item.href
                    ? isScrolled
                      ? 'text-navy-900 bg-slate-100'
                      : 'text-white bg-white/20'
                    : isScrolled
                    ? 'text-slate-600 hover:text-navy-900 hover:bg-slate-50'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/contact">
              <Button
                variant={isScrolled ? 'outline' : 'ghost'}
                className={`${
                  isScrolled
                    ? 'border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white'
                    : 'text-white border-white/30 hover:bg-white/10'
                }`}
              >
                Contact Us
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                className={`${
                  isScrolled
                    ? 'bg-teal-600 hover:bg-teal-700 text-white'
                    : 'bg-white text-navy-900 hover:bg-white/90'
                }`}
              >
                Request Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                className={isScrolled ? 'text-navy-900' : 'text-white'}
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-white">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex flex-col h-full pt-8">
                <div className="flex items-center gap-3 mb-8">
                  <img 
                    src="/images/logo-dark.png" 
                    alt="Naviton Global" 
                    className="h-12 w-auto"
                  />
                </div>
                <nav className="flex flex-col gap-1">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className={`px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                        location.pathname === item.href
                          ? 'bg-slate-100 text-navy-900'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-navy-900'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </nav>
                <div className="mt-auto pb-8 flex flex-col gap-3">
                  <Link to="/contact" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full border-navy-900 text-navy-900">
                      Contact Us
                    </Button>
                  </Link>
                  <Link to="/contact" onClick={() => setIsOpen(false)}>
                    <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                      Request Quote
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
